import React from "react";
import { CARD_LANGUAGE_NAMES, CARD_LANGUAGES, fmtKm, gameLabel, GRADE_NAMES, GRADES, languagesForGames, tradingName, tradingHandle } from "./data.jsx";
import { api, expansionNameFor } from "./api.jsx";
import { hostingShop } from "./hours.jsx";
import { track } from "./analytics.jsx";
import { useApp } from "./store.jsx";
import { PlacePicker, MeetWhen } from "./location.jsx";
import { CardAutocomplete } from "./autocomplete.jsx";
import { ModalHost, BackLink, Linked, CardmarketMark, CardTraderMark, cardmarketProfileUrl } from "./chrome.jsx";
import { PictureReport } from "./picture-report.jsx";
import { PictureFrame, PrintingTiles, printingTilesOf } from "./printing-tiles.jsx";

const { Button, IconButton, Icon, Badge, Card, Tag, Select, Input, Checkbox, Radio, ConditionChip, DistanceTag, TraderRow, ListingCard, FluteDivider, EmptyState } = window.CardboardmarketDesignSystem_2a3239;
// Ours, not the design system's: Back closes it (chrome.jsx).
import { Dialog } from "./chrome.jsx";

/**
 * Proposing a trade.
 *
 * The meetup step is deliberately optional. Agreeing to swap and agreeing where to
 * meet are two different conversations, and making someone pick a café before they
 * know the other person is even interested puts the harder question first. The
 * recipient settles the meetup when they accept.
 */
function ProposeTradeDialog({ listing, open, onClose }) {
  const { myCards, proposeTrade, myShop, t } = useApp();
  const [step, setStep] = React.useState(1);
  const [offer, setOffer] = React.useState([]);
  /*
   * Cards offered that are NOT listings of yours.
   *
   * This dialog used to offer you a choice of your own active listings and
   * nothing else, which quietly required you to publish a card before you could
   * offer it. That is the wrong shape for this product: a listing is an
   * advertisement, and nobody advertises the whole binder. Most of what anybody
   * would trade away has never been listed and never will be.
   *
   * The API has always allowed this — `cardId` is optional on a trade item, and
   * the ownership check only fires for items that carry one. So an item added
   * here is a claim by name, exactly like the cash figure next to it: unverified
   * until the two of you meet, which is the only place anything is verified.
   */
  const [extras, setExtras] = React.useState([]);
  const [pick, setPick] = React.useState('');
  const [pickSet, setPickSet] = React.useState('');
  const [pickGrade, setPickGrade] = React.useState('NM');
  const [cash, setCash] = React.useState('');
  const [placeName, setPlaceName] = React.useState('');
  const [placeNote, setPlaceNote] = React.useState('');
  const [when, setWhen] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  /*
   * How many copies are being asked for.
   *
   * Held as a NUMBER rather than the input's string, because it is bounded
   * against another number in three places below and a string comparison
   * against a maximum is the kind of thing that works until somebody lists ten.
   */
  const [want, setWant] = React.useState(1);
  /*
   * WHETHER THE PERSON HAS TOUCHED THE MONEY, which is the whole difficulty with
   * seeding a field.
   *
   * The box starts at the seller's asking price so a buyer is answering a number
   * rather than inventing one — the owner's report: "I don't see a pre-filled
   * price with the price asked in the listing. That would be nice." But asking
   * for a second copy has to move that number too, or the default silently
   * becomes wrong the moment anybody uses the copies picker.
   *
   * So it re-seeds on every change to `want`, and stops the instant somebody
   * types. A field that overwrites what you just typed because you then changed
   * the quantity is worse than one that never helped at all.
   */
  const [cashTouched, setCashTouched] = React.useState(false);
  const asking = listing.priceCents != null ? listing.priceCents * want : null;
  React.useEffect(() => {
    if (!open) return;
    if (cashTouched) return;
    setCash(asking == null ? '' : api.moneyText(api.formatCents(asking)));
  }, [open, asking, cashTouched]);
  React.useEffect(() => {
    if (open) { setStep(1); setOffer([]); setExtras([]); setPick(''); setPickSet(''); setWant(1); setCashTouched(false); }
  }, [open]);
  if (!open) return null;
  const seller = listing.seller || {};
  /*
   * What may still be asked for.
   *
   * `available`, not `quantity`: copies promised to accepted trades are gone as
   * far as a new proposal is concerned, and offering to pick four when one is
   * free would be a form that leads to a refusal at acceptance — the worst
   * moment to learn it, because by then two people have agreed a café.
   *
   * Floored at one so the picker is never empty. A listing with nothing free is
   * still worth proposing against: the seller may cancel one of the trades
   * holding it, and a proposal is an ask rather than a promise.
   */
  const stock = listing.stock || { quantity: 1, committed: 0, available: 1 };
  const most = Math.max(1, stock.available);
  /*
   * `from` says where each entry came from, rather than leaving the remove
   * button to infer it from `cardId` being present. cardId is optional on a
   * listing row, so inferring would silently fail to remove a listing that has
   * no card behind it — the same shape of bug as reading identity off a field
   * that is allowed to be absent.
   */
  const fromListings = myCards.filter((c) => offer.includes(c.id)).map((c) => ({ ...c, from: 'listing' }));
  const chosen = fromListings.concat(extras.map((c) => ({ ...c, from: 'named' })));
  /*
   * The cash field holds what was typed. Both the thing we SEND and the thing we
   * SHOW want cents, so the conversion happens once, here, under a name that says
   * which unit came out. It used to happen inline on the way to the API while the
   * summary below rendered the raw string with a "€" stuck on it.
   *
   * It was also `Math.round(Number(cash) * 100)` over a digits-only string of
   * WHOLE EUROS, because the input above stripped the separator as it was typed.
   * Cash on top is the half of a trade most likely to be a few cents either way —
   * "your card plus 3,50" — and it could only ever be a round euro.
   *
   * `undefined` rather than 0 where there is no number, because that is what the
   * API means by absent: `cashCents` there has `minimum: 1`, so an offer of zero
   * cash is a 400 and not a smaller offer.
   */
  const cashCents = api.centsFromMoney(cash) || undefined;
  /*
   * A proposal needs a card or some money in it, and the money has to be money —
   * a box holding «,» is text, not an amount, and letting it stand for one would
   * put a proposal with nothing in it in front of the server.
   */
  const valid = step === 1 ? (chosen.length > 0 || cashCents > 0) : true;

  const addExtra = () => {
    const title = pick.trim();
    if (!title) return;
    // A card already selected from your listings is the same card; adding it
    // again would send it twice and offer it twice.
    const already = chosen.some((c) => c.title.toLowerCase() === title.toLowerCase());
    if (!already) {
      setExtras(extras.concat([{ id: `x${extras.length}-${title}`, title, set: pickSet || undefined, condition: pickGrade }]));
    }
    setPick('');
    setPickSet('');
  };

  const send = async () => {
    /*
     * A TIME THE SHOP IS SHUT NEVER LEAVES THE SCREEN. The field beside this
     * already says so in red; sending it anyway would trade a sentence the
     * reader can act on for a 422 the store renders as a different one.
     */
    setBusy(true);
    try {
      await proposeTrade({
        listingId: listing.id,
        quantity: want,
        items: [
          // The count rides on the trade, not on the item line: one trade is for
          // n copies of one listing, and putting a number on the item as well
          // would give the same fact two places to be wrong in.
          { direction: 'requested', title: listing.title, grade: listing.condition },
          // c.id is the LISTING; c.cardId is the card. Sending the former made the
          // server answer "card_not_yours", which was true and unhelpful.
          //
          // A card added by name has no cardId, and the key is LEFT OUT rather
          // than set to null: the body schema takes `cardId` as a uuid string, so
          // an explicit null would be rejected outright now that undeclared and
          // ill-typed fields are no longer silently stripped.
          ...chosen.map((c) => Object.assign(
            { direction: 'offered', title: c.title, grade: c.condition },
            c.cardId ? { cardId: c.cardId } : {},
          )),
        ],
        cashCents,
        placeName: placeName.trim() || undefined,
        placeNote: placeNote.trim() || undefined,
        /* Dropped by the server for a shop trade anyway — not sent, so no
           client ever has to be the one that remembered. */
        meetAt: when ? new Date(when).toISOString() : undefined,
      });
      onClose();
    } catch (e) {
      // The store has already said what went wrong; leave the dialog open so the
      // proposal is not silently lost.
    } finally { setBusy(false); }
  };

  return (
    <ModalHost>
      <Dialog open closeLabel={t("Close")} width={520} onClose={onClose}
        title={step === 1 ? t("What are you offering?") : t("Where and when?")}
        description={step === 1
          /*
           * What you are offering AGAINST — their card, its grade, and what they
           * are asking for it.
           *
           * This was an English template literal with the price glued to a "€",
           * and it was left that way once already because the words and the
           * number are the same problem: you cannot translate the sentence
           * without deciding where the symbol goes, and es-ES puts it on the
           * other end. Both halves, together — the sentence through t(), the
           * money through formatCents, which is the only thing that knows the
           * reader writes 12,50 € and not €12.5.
           */
          ? (listing.priceCents != null
            ? t("For {title} · {condition} · {price} asked", {
              title: listing.title,
              condition: listing.condition,
              price: api.formatCents(listing.priceCents),
            })
            : t("For {title} · {condition}", { title: listing.title, condition: listing.condition }))
          // fmtKm's floor is the same privacy rule DistanceTag enforces, and its
          // words were English here because this file is where the sentence is
          // built. The distance and the sentence around it are translated
          // together: half a sentence in each language is worse than either.
          : t("{name} is {distance} from you.", {
            name: tradingName(seller),
            distance: fmtKm(listing.km, t("less than 1 km")),
          })}
        footer={<>
          <Button variant="ghost" onClick={step === 1 ? onClose : () => setStep(1)}>{step === 1 ? t("Cancel") : t("Back")}</Button>
          {step === 1
            ? <Button disabled={!valid} onClick={() => setStep(2)}>{t("Next")}</Button>
            : <Button iconRight="send" disabled={busy} onClick={send}>{busy ? t("Sending\u2026") : t("Send proposal")}</Button>}
        </>}>
        {step === 1 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {/*
              How many copies, and only where there is a choice to make.

              A listing of one is the ordinary case and asking "how many?" about
              it is a question with one answer — a control that can only be set
              to the value it already has is noise on the screen where somebody
              is deciding whether to trade at all.

              Clamped on the way in rather than validated on the way out: the
              field cannot hold a number above what is free, so there is no
              error state to render and no proposal that looks fine until the
              seller tries to accept it. The seller's own limit is enforced
              again at acceptance, which is where it actually binds.
            */}
            {stock.quantity > 1 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Input
                  label={t("How many copies?")}
                  inputMode="numeric"
                  wrapperStyle={{ maxWidth: 160 }}
                  value={String(want)}
                  onChange={(e) => {
                    const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10);
                    setWant(!n ? 1 : Math.min(n, most));
                  }}
                />
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {stock.available > 0
                    ? t("{available} of {quantity} still free.", { available: stock.available, quantity: stock.quantity })
                    : t("All {quantity} are promised to trades already agreed. You can still ask — the seller may free one up.", { quantity: stock.quantity })}
                </span>
              </div>
            ) : null}
            {/*
              Your own listings, as a shortcut — every one of them.
              This was `myCards.slice(0, 5)`, so a trader with six listings could
              not offer the sixth and was never told why. A scrollable list is the
              honest version: nothing is hidden, and the dialog still fits.
            */}
            {myCards.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="cbm-label">{t("From your listings")}</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
                  {myCards.map((c) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: '8px 10px', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)', background: offer.includes(c.id) ? 'var(--surface-sunken)' : 'var(--white)' }}>
                      <Checkbox checked={offer.includes(c.id)} onChange={(on) => setOffer(on ? [...offer, c.id] : offer.filter((x) => x !== c.id))} label={c.title} description={c.set} />
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ConditionChip grade={c.condition} label={t(GRADE_NAMES[c.condition] || c.condition)} />
                        {c.priceCents != null ? <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{api.formatCents(c.priceCents)}</span> : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="cbm-label">{myCards.length ? t("Or any other card you have") : t("What are you putting up?")}</span>
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("It does not have to be listed. Name what you are bringing — you hand it over in person either way.")}
              </span>
              <CardAutocomplete
                label={undefined}
                value={pick}
                onChange={(v) => { setPick(v); setPickSet(''); }}
                onPick={(item) => { setPick(item.name); setPickSet(item.set || ''); }}
                onEnter={addExtra}
              />
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <Select label={t("Condition")} value={pickGrade} onChange={setPickGrade}
                    options={GRADES.map((g) => ({ value: g, label: `${g} · ${t(GRADE_NAMES[g])}` }))} />
                </div>
                <Button variant="outline" iconLeft="plus" disabled={!pick.trim()} onClick={addExtra}>{t("Add")}</Button>
              </div>
            </div>

            {/*
              What is actually on the table, in one place.
              Two ways to add a card means two lists to read before you can answer
              "what am I sending?", so the answer is written out instead. Anything
              added by name can be taken off again; a listing is unticked above.
            */}
            {chosen.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="cbm-label">{t("You are offering")}</span>
                {chosen.map((c) => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: '8px 10px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)' }}>
                    <span style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>{c.title}</span>
                    <ConditionChip grade={c.condition} label={t(GRADE_NAMES[c.condition] || c.condition)} />
                    <div style={{ marginLeft: 'auto' }}>
                      <IconButton icon="x" label={t("Remove")} onClick={() => c.from === 'listing'
                        ? setOffer(offer.filter((x) => x !== c.id))
                        : setExtras(extras.filter((x) => x.id !== c.id))} />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {/* `decimal`, not `numeric`: a phone keyboard set to `numeric` has
                no separator key at all, so the field would still refuse cents on
                the device this is mostly used from. The copies field above stays
                `numeric` — that one really is whole units. */}
            {/*
              THE LABEL FOLLOWS THE LISTING, because the same box means two
              different things. Against a card offered for sale the money IS the
              offer, and calling it "on top" describes a tip. Against one offered
              for trade there is no asking price and it genuinely is an extra
              beside the cards.
            */}
            <Input
              label={asking == null ? t("Cash on top (optional)") : t("What you pay")}
              suffix="€" inputMode="decimal" value={cash}
              onChange={(e) => { setCashTouched(true); setCash(api.moneyText(e.target.value)); }}
              hint={asking == null
                ? t("Pick a card, add cash, or both.")
                : t("They are asking {price}. Offer what you like \u2014 they can answer with a different number.", { price: api.formatCents(asking) })} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/*
              NOT SAID WHEN A SHOP IS HOSTING, because then it is not true. The
              owner, on a shop's listing: "'Opcional. Propón un sitio si tienes
              uno en mente…' should go. It's not optional. The place is always
              the shop itself."
              `PlacePicker` already turns itself into a statement in that case —
              the counter, named, with nothing to choose — so this line was the
              last thing on the screen still offering a choice nobody has. The
              server refuses anywhere else, which is what made it a lie rather
              than just clutter.
            */}
            {hostingShop(listing.sellerShop, myShop) ? null : (
              <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                {t("Optional. Suggest a place if you have one in mind — otherwise {name} picks when they accept.", { name: tradingName(seller) })}
              </span>
            )}
            {/* The same place search the other side has had all along. The
                proposer is the one who has never been there, so if either of them
                needs help finding a real café it is this one. */}
            <PlacePicker
              name={placeName} onName={setPlaceName}
              note={placeNote} onNote={setPlaceNote}
              // Their own shop first, where the seller is one. No `onPin`: a
              // proposer cannot drop a pin, which is why `pickShop` guards it.
              theirShop={listing.sellerShop}
              /*
               * AND MINE, for the case the listing cannot describe: a shop
               * proposing on a person's card. The trade still happens at the
               * counter — the server says so — and without this the form would
               * offer a café and the server would refuse it.
               */
              myShop={myShop}
            />
            {/*
              NO TIME FIELD AT ALL WHEN A SHOP IS HOSTING. The owner's rule:
              "shops already go by their opening hours, so a regular trader
              making a trade proposal should not be required to use a time.
              Actually, we should get rid of the possibility altogether."
              The hours are on the counter card above this, which is where
              somebody reads them and decides when to turn up.
            */}
            {hostingShop(listing.sellerShop, myShop)
              ? null
              : <MeetWhen value={when} onChange={setWhen} host={null} />}
            <Card tone="sunken" padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="cbm-label">{t("You send")}</span>
              <span style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
                {/* "× 3" rather than a sentence: a multiplication sign reads the
                    same in both languages and needs no entry in a dictionary. */}
                {chosen.map((c) => c.title).concat(cashCents != null ? [api.formatCents(cashCents)] : []).join(' + ') || t("Nothing yet")} for {listing.title}{want > 1 ? ` × ${want}` : ''}
              </span>
            </Card>
          </div>
        )}
      </Dialog>
    </ModalHost>
  );
}

/**
 * ONE PICTURE, AS BIG AS THE READER NEEDS IT.
 *
 * A card is 3:4 and the page shows it in a 420px column, which on a phone is a
 * thumbnail of the thing the whole listing is about. Every claim a listing makes
 * about condition — the whitening on a back edge, an off-centre cut, a soft
 * corner — is a claim about detail that column is too small to carry. So the
 * picture opens.
 *
 * It shows the FULL file, not the thumbnail. Both travel with every frame, and
 * blowing up a 400px thumbnail to fill a window is the one thing this must not
 * do: it would look like enlarging and would show strictly less than the page
 * already had. Only the frame being looked at is fetched — the artwork on this
 * site averages 348 kB and reaches 2.7 MB, and a viewer that warmed all of them
 * on open would cost more than the page it sits over.
 *
 * AND IT MAGNIFIES, because filling the window is not enough on the device this
 * product is drawn for. Measured on a Pixel 7: the picture on the page is 380px
 * wide and fitted to the window it is 388px — two per cent, for a control the
 * reader had to find and press. The page already gives a 3:4 card the full width
 * of a phone, so there is no room left to win. On a 1280x900 desktop the same
 * step goes from 560px tall to 756px, which is why fitting looks like a feature
 * until you hold a phone.
 *
 * So the picture also opens at its own pixels. The factor is the image's natural
 * width over the fitted width — 2.3x for a 900px photograph on that phone —
 * clamped to a floor of 2 so the control always does something visible and a
 * ceiling of 4 so a small file is not smeared across the screen. Panning is the
 * pane's own scrollbars rather than drag handlers: a browser already knows how to
 * do this with a finger, a trackpad, a wheel and a keyboard, and every one of
 * those is a case a hand-written implementation gets wrong separately.
 *
 * `ModalHost` rather than a fixed div of our own, and that is not ceremony. A
 * z-index only competes inside its own stacking context, and this morning a
 * dialog opened from the browse rail rendered UNDER the listing grid because
 * `.cbm-rail__aside` is sticky and sticky creates one. The host portals to
 * `document.body`, so a picture cannot be trapped by wherever it was written.
 *
 * Dismissable three ways, because people reach for different ones: Escape, the
 * corner button, and the dark ground around the picture. The picture itself is
 * not one of them — tapping the thing you came to look at should not take it
 * away, so a tap there magnifies instead, which is the other thing a thumb on a
 * photograph is asking for.
 */
function ListingLightbox({ frames, index, onIndex, onClose, title }) {
  const { t } = useApp();
  const surface = React.useRef(null);
  const pane = React.useRef(null);
  const picture = React.useRef(null);
  /* The magnified size in pixels, or null for fitted. Pixels rather than a
     multiplier because the fitted size is whatever the window made it, and a
     percentage inside a flex child with `auto` height does not resolve. */
  const [big, setBig] = React.useState(null);
  const many = frames.length > 1;
  const frame = frames[index];

  /*
   * The keys, bound to the window rather than to the surface.
   *
   * Escape has to work whatever holds focus inside the dialog, and after a click
   * on the ground that is the surface, while after a tap on Next it is a button.
   * A handler on one element answers for one of those.
   */
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (!many) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); onIndex((index + 1) % frames.length); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); onIndex((index - 1 + frames.length) % frames.length); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, frames.length, many, onClose, onIndex]);

  /*
   * Focus moves INTO the dialog when it opens.
   *
   * Without it focus stays on the image button behind, so a keyboard reader is
   * told nothing has happened and their first Tab walks the page underneath. The
   * surface takes it rather than the close button: landing on "close" announces
   * the way out before the thing itself.
   */
  React.useEffect(() => {
    if (surface.current) surface.current.focus();
  }, []);

  /* The page behind does not scroll under it: the ModalHost it renders in locks
     <html>. No lock of its own on <body> — with <html> already hidden, that
     makes <body> a scroll container and un-sticks every sticky element behind
     (see ModalHost in chrome.jsx). */

  /* A different photograph is a different look at the card, so it starts fitted.
     Staying magnified would leave the reader somewhere in the middle of a picture
     they have not seen the whole of yet. */
  React.useEffect(() => { setBig(null); }, [index]);

  /*
   * Centred on the middle of the picture when it grows.
   *
   * Layout effect, not effect: the scroll offsets are read and written against a
   * size the browser has just computed, and doing that after paint shows one
   * frame of the top-left corner before it jumps.
   */
  React.useLayoutEffect(() => {
    const el = pane.current;
    if (!el || !big) return;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
  }, [big]);

  const step = (by) => onIndex((index + by + frames.length) % frames.length);

  /*
   * In to the file's own pixels, out to the window.
   *
   * `naturalWidth / width` is the factor that shows one image pixel per screen
   * pixel, which is the most detail there is to show and the point past which
   * magnifying is just interpolation. Floored at 2 so a photograph smaller than
   * the window still visibly does something when the control is pressed, and
   * capped at 4 so a small one is not smeared across it.
   */
  const zoom = () => {
    const el = picture.current;
    if (!el) return setBig(null);
    if (big) return setBig(null);
    const box = el.getBoundingClientRect();
    if (!box.width) return;
    const factor = Math.min(4, Math.max(2, (el.naturalWidth || 0) / box.width));
    setBig({ w: Math.round(box.width * factor), h: Math.round(box.height * factor) });
  };

  return (
    <ModalHost>
      <div
        ref={surface}
        className="cbm-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={t("{title}, full size", { title })}
        tabIndex={-1}
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, background: 'var(--bg-inverse)',
          display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
          padding: 'var(--space-4)', outline: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)', flex: '0 0 auto' }}>
          <span style={{ font: 'var(--type-meta)', color: 'var(--kraft-300)' }}>
            {many ? t("Picture {n} of {total}", { n: index + 1, total: frames.length }) : ''}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            {/* The keyboard's and the screen reader's way in. Tapping the picture
                does the same thing, which is what a thumb reaches for, but a
                convenience on an image is not an affordance anybody else has. */}
            <IconButton
              icon={big ? 'zoom-out' : 'zoom-in'}
              label={big ? t("Fit the picture to the window") : t("Zoom in")}
              size="lg"
              onClick={(e) => { e.stopPropagation(); zoom(); }}
            />
            <IconButton icon="x" label={t("Close the picture")} size="lg" onClick={onClose} />
          </div>
        </div>

        {/*
          `minHeight: 0` is what makes fitting to the window work at all: a flex
          child defaults to its content's minimum, so the picture would push this
          row past the bottom of the screen instead of being bounded by it — and
          the phone that most needs fitting is the one it would run off.

          `margin: auto` on the picture rather than `justify-content: center` on
          this, and only one of those survives being magnified. A centred flex
          child that overflows its container is unreachable at the start: the
          scrollbar cannot move to negative space, so the top-left quarter of a
          magnified picture simply cannot be looked at. Auto margins centre the
          same way while there is room and collapse to nothing when there is not.

          The pane itself does not swallow clicks. The dark either side of a 3:4
          picture in a 3:2 window is where a reader clicks to get out, and making
          the whole middle of the screen inert would leave only two thin strips.
        */}
        <div
          ref={pane}
          style={{ flex: 1, minHeight: 0, display: 'flex', overflow: big ? 'auto' : 'hidden' }}
        >
          <img
            ref={picture}
            src={frame.full}
            alt={title}
            onClick={(e) => { e.stopPropagation(); zoom(); }}
            style={big
              ? { width: big.w, height: big.h, maxWidth: 'none', maxHeight: 'none', flex: '0 0 auto', margin: 'auto', cursor: 'zoom-out', borderRadius: 'var(--radius-sm)' }
              : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', margin: 'auto', cursor: 'zoom-in', borderRadius: 'var(--radius-sm)' }}
          />
        </div>

        {many ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-4)', flex: '0 0 auto' }}>
            <IconButton icon="chevron-left" label={t("Previous picture")} size="lg"
              onClick={(e) => { e.stopPropagation(); step(-1); }} />
            <IconButton icon="chevron-right" label={t("Next picture")} size="lg"
              onClick={(e) => { e.stopPropagation(); step(1); }} />
          </div>
        ) : null}
      </div>
    </ModalHost>
  );
}

/**
 * The pictures of a listing: the publisher's, and the trader's own.
 *
 * The order is the argument. The catalogue image says what the card looks like
 * in print — useful for recognising it, and identical for every copy in
 * existence. The trader's photographs say what THIS copy looks like: the
 * whitening on the back edge, the off-centre cut, the soft corner. That is the
 * entire substance of a condition claim, and until now the only evidence for one
 * was a grade somebody typed into a box.
 *
 * So the trader's photos lead when there are any, and the publisher's picture
 * becomes one more frame in the strip rather than the headline.
 */
function ListingGallery({ listing, settled }) {
  const { t } = useApp();
  const photos = listing.photos || [];

  /*
   * EVERY HOOK ABOVE THE FIRST `return`, which `shown` was not.
   *
   * `settled` is false on the first render and true on the next, on the same
   * mounted component — that is the entire point of it — and `shown` was declared
   * BELOW the skeleton's early return. That is a hook count that changes between
   * two renders of one component, and the only reason it never threw is an
   * accident of how React picks its dispatcher: a previous render that registered
   * NO hooks leaves `memoizedState` null, which React reads as a mount and
   * handles. Checked against the previous build before writing this down, because
   * "surely that crashes" was the obvious conclusion and it is wrong.
   *
   * The accident holds for exactly one hook declared after exactly one early
   * return with nothing above it. A second piece of state is what this change
   * adds, so the arrangement stops being safe-by-luck and the hooks come up here
   * where the rule can be read off the file.
   */
  const [shown, setShown] = React.useState(0);
  /* Which frame is open at full size, or nothing. Held HERE rather than inside
     the viewer so closing it leaves the strip on the picture you were looking
     at, and so the viewer stays a thing that renders what it is handed. */
  const [zoomed, setZoomed] = React.useState(false);

  /*
   * A skeleton until we know what the pictures ARE.
   *
   * The browse list carries the catalogue image and no photographs; the detail
   * carries both. The page paints from the browse copy immediately so the title,
   * price and seller are there at once — but that means the image area would show
   * the stock picture and then swap to the seller's a moment later, which is a
   * visible jump and reads as a glitch. Reported exactly that way.
   *
   * Everything else on the page is correct in the browse copy and paints straight
   * away. Only this box has to wait, because only this box is about to change.
   */
  if (!settled) {
    return (
      <>
        <div
          aria-hidden="true"
          style={{
            width: '100%', aspectRatio: '3/4', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-hairline)',
            background: 'linear-gradient(100deg, var(--kraft-100) 30%, var(--kraft-200) 50%, var(--kraft-100) 70%)',
            backgroundSize: '200% 100%',
            animation: 'cbm-shimmer 1.4s var(--ease-standard) infinite',
          }}
        />
        {/* Said, not just drawn: a shimmering rectangle means nothing to a screen
            reader, and "an image is coming" is the whole content of this state. */}
        <span role="status" style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>
          {t("Loading the pictures\u2026")}
        </span>
      </>
    );
  }
  // The stock image is a frame too, marked so, and last: it is the least
  // informative picture on the page once a real one exists.
  /*
   * `srcset` is the CATALOGUE picture's ladder and nothing else's.
   *
   * A trader's photograph already arrives in two sizes — `url` and `thumbUrl`,
   * cut when it was uploaded — so it has its choice made for it and carries
   * null. The stock frame had no such thing: `full` and `thumb` were the SAME
   * url, so the 56px filmstrip cell was pulling the whole artwork to draw a
   * stamp. That is what the srcset fixes here.
   *
   * `full` STAYS THE ORIGINAL ON EVERY FRAME. The lightbox below magnifies to
   * one image pixel per screen pixel and reads `full`; it is handed no srcset at
   * all, deliberately, because the one screen whose purpose is detail must not
   * be given permission to fetch less of it.
   */
  const frames = photos
    .map((p) => ({ key: p.id, full: p.url, thumb: p.thumbUrl, srcset: null, stock: false }))
    .concat(listing.image && !photos.some((p) => p.thumbUrl === listing.image)
      ? [{ key: 'stock', full: listing.image, thumb: listing.image, srcset: listing.imageSrcset || null, stock: true }]
      : []);
  // Clamped rather than reset: removing a photo should not throw you back to the
  // first one, and an index past the end renders nothing at all.
  const at = Math.min(shown, frames.length - 1);
  const active = frames[at];

  if (!frames.length) {
    return (
      <>
        <div className="cbm-photo">{t("No picture for this one")}</div>
        <span style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>
          {t("Check the card at the meetup before you pay.")}
        </span>
        {/* The other half of the report: a card we have no picture for at all. */}
        <PictureReport game={listing.game} cardCode={listing.cardCode} productId={listing.sourceProductId} kind="missing" />
      </>
    );
  }

  return (
    <>
      {/*
        The picture, and a way into a bigger one.

        A button around it rather than a click handler on the image: this is the
        only control on the page that opens the viewer, and an `img` with an
        `onClick` is reachable by a mouse and by nothing else — no Tab, no Enter,
        no announcement that it does anything at all. The button carries no box of
        its own, so the image is still the only thing drawn.

        The corner chip is there because a phone has no cursor to change. `zoom-in`
        tells a desktop reader what this is and tells a touch reader nothing, and a
        feature nobody can see is one nobody uses.
      */}
      <button
        type="button"
        onClick={() => setZoomed(true)}
        aria-label={t("Enlarge the picture")}
        style={{ position: 'relative', display: 'block', width: '100%', padding: 0, border: 0, background: 'none', cursor: 'zoom-in', lineHeight: 0 }}
      >
        <img
          src={active.full}
          /* The hero is 420 CSS px at every desktop width and the viewport
             minus its gutter below 484, so those are the two answers. A browser
             with no srcset ignores both and renders `src`, which is the
             original — the behaviour of every picture on this page before
             there were variants. */
          srcSet={active.srcset || undefined}
          sizes="(max-width: 484px) calc(100vw - 32px), 420px"
          alt={listing.title}
          loading="lazy"
          style={{ width: '100%', aspectRatio: '3/4', objectFit: 'contain', background: 'var(--kraft-100)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)' }}
        />
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', right: 10, bottom: 10, width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--white)', color: 'var(--ink-700)',
            border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          <Icon name="maximize-2" size={16} />
        </span>
      </button>
      {zoomed ? (
        <ListingLightbox
          frames={frames}
          index={at}
          onIndex={setShown}
          onClose={() => setZoomed(false)}
          title={listing.title}
        />
      ) : null}
      {frames.length > 1 ? (
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {frames.map((f, i) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setShown(i)}
              aria-label={f.stock ? t("The publisher’s picture") : t("Photo {n}", { n: i + 1 })}
              aria-current={i === shown}
              style={{
                padding: 0, cursor: 'pointer', background: 'none', borderRadius: 'var(--radius-sm)',
                border: `2px solid ${i === shown ? 'var(--ink-900)' : 'var(--border-hairline)'}`,
                lineHeight: 0,
              }}>
              {/* 56 px at every viewport — no media query touches this strip. */}
              <img src={f.thumb} srcSet={f.srcset || undefined} sizes="56px" alt="" loading="lazy"
                style={{ width: 56, height: 72, objectFit: 'cover', borderRadius: 'var(--radius-xs)' }} />
            </button>
          ))}
        </div>
      ) : null}
      <span style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>
        {active.stock
          ? t("The publisher’s picture. Check the actual card at the meetup before you pay.")
          : t("The seller’s own photo of this copy. Check the card at the meetup before you pay.")}
      </span>
      {/* Only under the CATALOGUE's picture — see PictureReport. A seller's own
          photograph is of the card in their hand and there is nothing for us to
          correct in it. */}
      {active.stock ? <PictureReport game={listing.game} cardCode={listing.cardCode} productId={listing.sourceProductId} kind="wrong" /> : null}
    </>
  );
}

/**
 * AN OPERATOR CORRECTING THIS LISTING'S PRINTING.
 *
 * The owner's design, 2026-10-20: a listing whose words say one card and
 * whose printing is another (production had a "Destined Rivals" pointing at a
 * Live Code Card) is put right from here, by an operator. The PRINTING, with
 * the language and the finish, and nothing of the seller's: the price, the
 * condition, the copies and the photographs are not on this form at all. The
 * title and the set follow the catalogue for the product chosen — the server
 * derives them, so nothing typed here can disagree with the printing.
 *
 * The card is chosen with the post form's own typeahead (`CardAutocomplete`,
 * as it is), and the exact printing from the catalogue's list of them for that
 * card (`/api/catalog/printings`) — the suggestion alone names one product of
 * a card printed in many sets. Every option carries its Cardmarket id,
 * because an operator correcting a printing is checking against one.
 *
 * A listing mirrored from a shop's CardTrader stock is refused by the server
 * (`managed_on_cardtrader`); the dialog says where the fix belongs instead of
 * a toast saying no.
 */
function FixListingDialog({ listing, onClose, onFixed }) {
  const { notify, t } = useApp();
  const [title, setTitle] = React.useState('');
  const [picked, setPicked] = React.useState(null);
  // The printings as tiles grouped by set — see printing-tiles.jsx.
  const [options, setOptions] = React.useState({ loading: false, groups: [] });
  const [productId, setProductId] = React.useState('');
  const [language, setLanguage] = React.useState(listing.language || '');
  const [foil, setFoil] = React.useState(!!listing.foil);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState(null);

  /*
   * The printings of the card picked — or, before anything is picked, of the
   * card the listing is on now, so an operator who only needs another copy of
   * the same card sees it at once, marked "Current", without typing. Nothing
   * is chosen until a tile is.
   */
  const seed = picked ? picked.productId : listing.sourceProductId;
  React.useEffect(() => {
    if (!seed) return undefined;
    const controller = new AbortController();
    // The suggestion's own product, as a tile with no picture, for when the
    // list does not hold it or cannot be had.
    const ownSet = picked ? (picked.setName || picked.cardCode || '') : '';
    const ownLabel = picked ? (expansionNameFor({ expansion: ownSet, expansionEs: picked.expansionEs }) || ownSet) : '';
    const own = picked
      ? { key: 'own', set: ownSet, label: ownLabel, tiles: [{ productId: picked.productId, set: ownSet, label: ownLabel, version: null, picture: null }] }
      : null;
    setOptions({ loading: true, groups: [] });
    if (picked) setProductId(String(picked.productId));
    api.cardPrintings({ productId: seed }, controller.signal)
      .then((r) => {
        const groups = printingTilesOf(r);
        const held = !own || groups.some((g) => g.tiles.some((x) => String(x.productId) === String(own.tiles[0].productId)));
        setOptions({ loading: false, groups: held ? groups : [own, ...groups] });
      })
      .catch((e) => { if (e.name !== 'AbortError') setOptions({ loading: false, groups: own ? [own] : [] }); });
    return () => controller.abort();
  }, [seed]);

  const chosenTile = options.groups.flatMap((g) => g.tiles).find((x) => String(x.productId) === productId) || null;

  const game = (picked && picked.game) || listing.game;
  const languages = CARD_LANGUAGES
    .filter((code) => languagesForGames(game ? [game] : []).includes(code) || code === language)
    .map((code) => ({ value: code, label: t(CARD_LANGUAGE_NAMES[code]) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const save = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      await api.adminFixListingPrinting(listing.id, { sourceProductId: Number(productId), language, foil });
      notify(t("Listing corrected. The seller has a message saying what changed."));
      onFixed();
    } catch (e) {
      // THE CODE, NOT THE MESSAGE: `ApiError` turns a known code into a sentence.
      setRefusal((e && e.code) || 'unknown');
    } finally {
      setBusy(false);
    }
  };

  const REFUSALS = {
    managed_on_cardtrader: t("This listing comes from the seller's CardTrader stock, so it is read-only here. The printing has to be corrected on CardTrader; the next sync brings it across."),
    no_changes: t("That is already the printing, language and finish this listing has."),
    unknown_catalog_product: t("The catalogue does not hold that printing any more. Pick another."),
    listing_changed: t("Somebody changed this listing a moment ago. Close this and open it again."),
    not_found: t("This listing is no longer up, so there is nothing to correct."),
    not_an_operator: t("Only an operator can correct a listing."),
  };

  return (
    <ModalHost>
      <Dialog open width={520} closeLabel={t("Close")} onClose={onClose}
        title={t("Fix this listing")}
        description={t("Choose the printing the seller actually has. The title and the set are taken from the catalogue; the price, the condition, the copies and the photos stay as the seller set them.")}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
            <Button onClick={save} disabled={busy || !productId || !language || options.loading}>
              {busy ? t("Saving…") : t("Save the correction")}
            </Button>
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          {/* Before and after side by side, so the correction can be checked
              by eye. The frames hold their shape before anything is chosen. */}
          <div className="cbm-pfix__compare">
            <figure className="cbm-pfix__side">
              <PictureFrame picture={listing.image ? { url: listing.image, srcset: listing.imageSrcset } : null} sizes="72px"
                alt="" />
              <figcaption>
                <span className="cbm-pfix__line">{t("Now: {title} · {set} · #{id}", { title: listing.title, set: listing.set || '—', id: listing.sourceProductId ?? '—' })}</span>
              </figcaption>
            </figure>
            <figure className="cbm-pfix__side">
              <PictureFrame picture={chosenTile && chosenTile.picture} sizes="72px" />
              <figcaption>
                <span className="cbm-pfix__label">{t("Correction")}</span>
                <span className="cbm-pfix__line">
                  {chosenTile
                    ? `${[chosenTile.label || chosenTile.set || t("Set not named"), chosenTile.version].filter(Boolean).join(' | ')} · #${chosenTile.productId}`
                    : t("Choose a card and a printing below.")}
                </span>
              </figcaption>
            </figure>
          </div>
          <CardAutocomplete
            label={t("Card")}
            value={title}
            // Every game, not the listing's: the game may be the wrong half too, and
            // the server takes it from the product chosen.
            onChange={(v) => { setTitle(v); setPicked(null); setProductId(''); }}
            onPick={(item) => { setTitle(item.name); setPicked(item); setProductId(String(item.productId)); }}
          />
          {picked || options.loading || options.groups.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <span id="cbm-pfix-printing" style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>{t("Printing")}</span>
              <PrintingTiles labelledBy="cbm-pfix-printing" groups={options.groups} loading={options.loading}
                value={productId} current={listing.sourceProductId} onChange={(v) => setProductId(v)} />
            </div>
          ) : null}
          <Select label={t("Language")} value={language} onChange={(v) => setLanguage(v)}
            options={[{ value: '', label: t("Choose a language") }].concat(languages)} />
          <Checkbox label={t("Foil / Holo / Shiny")} checked={foil} onChange={(on) => setFoil(on)} />
          {refusal ? (
            <p role="alert" style={{ margin: 0, font: 'var(--type-body-sm)', color: 'var(--danger-600)' }}>
              {REFUSALS[refusal] || t("That did not save. Try again in a moment.")}
            </p>
          ) : null}
        </div>
      </Dialog>
    </ModalHost>
  );
}

export function ListingScreen() {
  const { route, listings, go, messageTrader, notify, point, t, trader } = useApp();
  /**
   * A listing arrives one of two ways: already in the loaded list, or as a shared
   * link opened cold. The second is the whole reason listings have their own URL,
   * so it fetches rather than falling back.
   *
   * It used to do `listings.find(...) || listings[0]`, which on a cold open showed
   * a DIFFERENT listing — worse than an error, because nothing looks wrong.
   *
   * IT NOW FETCHES EVEN WHEN THE LISTING IS ALREADY KNOWN, and the reason is that
   * the two copies are not the same thing. The browse list carries what a grid
   * tile needs; the detail endpoint carries everything, including the seller's
   * photographs, which browse deliberately omits so a page of forty cards does
   * not ship six images each.
   *
   * Reusing the browse copy therefore showed a listing page with no photographs
   * to anybody who arrived by clicking, while the same page opened from a link
   * worked perfectly — reported exactly that way. The known copy still paints
   * immediately so nothing feels slower; the full one replaces it when it lands.
   */
  const known = listings.find((l) => l.id === route.id);
  const [fetched, setFetched] = React.useState(null);
  const [missing, setMissing] = React.useState(false);
  // Bumped after an operator's correction, so the full record is fetched again and the page shows it.
  const [revision, setRevision] = React.useState(0);
  // Fetched WINS. It is the complete record; `known` is the summary that got the
  // page on screen.
  const listing = fetched || known;

  React.useEffect(() => {
    if (!route.id) return;
    let live = true;
    // Kept while the SAME listing is fetched again after a correction, so the page does not blank.
    setFetched((f) => (f && f.id === route.id ? f : null));
    api.listing(route.id, point ? { lat: point[0], lng: point[1] } : undefined)
      .then((r) => { if (live) setFetched(api.toListingView(r.listing)); })
      // Only an unknown listing is missing. A network blip on a listing already
      // on screen must not replace it with "not found".
      .catch(() => { if (live && !known) setMissing(true); });
    return () => { live = false; };
    // Coordinates as primitives: `point` is an array, and depending on it by
    // identity would refetch on every render that produced a new one.
  }, [route.id, point ? point[0] : null, point ? point[1] : null, revision]);

  const [propose, setPropose] = React.useState(false);
  const [fixing, setFixing] = React.useState(false);
  /*
   * Declared HERE, with the other hooks, and not beside the handler that uses it.
   * There is an early `return null` below for a listing that has not arrived yet,
   * and a hook after it runs on some renders and not others — which React reports
   * as "rendered more hooks than during the previous render" and which took the
   * whole screen down, not just this button.
   */
  const [revealing, setRevealing] = React.useState(null);

  React.useEffect(() => { if (listing) track('listing.view'); }, [listing && listing.id]);

  if (missing) {
    return (
      <>
        <BackLink label={t("Back to browse")} />
        <EmptyState icon="search" title={t("That listing is gone")}
          description={t("It may have been traded or withdrawn. There are others nearby.")}
          action={<Button onClick={() => go('browse')}>{t("Browse nearby cards")}</Button>} />
      </>
    );
  }
  if (!listing) return null;
  

  const seller = listing.seller || {};
  const more = listings.filter((l) => l.seller && l.seller.id === seller.id && l.id !== listing.id).slice(0, 3);
  const openThread = () => messageTrader(seller.id, tradingName(seller));

  /*
   * A LISTING THAT IS NOT UP STILL OPENS, AND OFFERS NOTHING TO ACT ON.
   *
   * The owner, 2026-10-16: a seller may take a listing down with a trade
   * accepted on it, "so nobody else could in the meantime send me a trade
   * proposal". The trade keeps its link here, so both people can see what they
   * agreed — which means this page is reached by a stranger with an old link
   * and by the other party of that trade alike, and for both it must say the
   * listing is down and start no new deal: no proposal, no message, no contact.
   * The server refuses a proposal and the contact (`not_found`), and a button
   * opening onto that refusal is a door onto a wall. Messaging addresses a
   * trader rather than a listing, so the server cannot refuse it here — hiding
   * it is this screen's own choice, so a dead listing is not a way in to its
   * seller. The trade itself is reached from the trades list and its
   * conversation, where messaging stays.
   *
   * Absent status is a browse copy, painted before the full record lands, and
   * browse only ever carries listings that are up.
   */
  const takesProposals = !listing.status || listing.status === 'active';
  // What a listing that is down says about itself: the badge and the sentence.
  const down = listing.status === 'traded'
    ? { badge: t("Already traded"), notice: t("This card has already been traded. It takes no new proposals.") }
    : { badge: t("Withdrawn"), notice: t("This listing has been taken down. It takes no new proposals, and a trade already agreed on it still stands.") };

  /*
   * Whether this seller offered a way to be reached. Presence only — the handle
   * itself is never in this payload, by design; see the api's listing route.
   */
  const reachable = (seller.reachable || {});
  // Offered, and offered on a listing that is up: a contact is a way to start a deal.
  const showsContact = takesProposals && Boolean(reachable.whatsapp || reachable.telegram);

  /**
   * Fetches the contact and opens it.
   *
   * Opened rather than displayed: the number is never put on screen or into
   * component state, so it cannot be screenshotted from here, read out of a React
   * tree, or left behind in a page somebody walks away from. The person gets
   * WhatsApp or Telegram, which is what they wanted.
   */
  const reveal = async (channel) => {
    setRevealing(channel);
    try {
      const { contact } = await api.listingContact(listing.id);
      const link = contact && contact[channel];
      if (!link) {
        // They switched it off between this page loading and the button being
        // pressed. Their answer is the current one, not the one we cached.
        notify(t("That trader is no longer reachable there."), 'caution');
        return;
      }
      window.open(link, '_blank', 'noopener,noreferrer');
    } catch (e) {
      /*
       * THE CODE, NOT THE MESSAGE. `ApiError` turns a known code INTO a
       * sentence — `not_authenticated` becomes "Sign in to do that." — so
       * `e.message.includes('not_authenticated')` was never true and both
       * branches below were dead. A guest pressing this got the generic
       * failure instead of being sent to sign in, which is the one case the
       * icons are shown to everybody for.
       */
      const reason = (e && e.code) || String((e && e.message) || e);
      if (reason.includes('not_authenticated')) {
        // A guest, which is the expected case rather than an error — the icons
        // are shown to everybody precisely so this offer has somewhere to happen.
        notify(t("Sign in to see how to reach this trader."), 'caution');
        go('join');
        return;
      }
      if (reason.includes('email_not_verified')) {
        /*
         * Signed in, but the address is unconfirmed — the same gate posting has.
         * Sent to settings rather than nowhere, because that screen is where the
         * "send it again" button lives, and a refusal that does not say what to
         * do next is just a wall.
         */
        notify(t("Confirm your email address to see how to reach traders. The app's own messages work either way."), 'caution');
        go('you', { tab: 'settings' });
        return;
      }
      notify(t("That could not be loaded."), 'caution');
    } finally {
      setRevealing(null);
    }
  };

  /**
   * Sharing a listing.
   *
   * The button existed and did nothing at all, which is worse than not having
   * one. The Web Share API is what a phone expects — it opens the system sheet,
   * so the link can go to WhatsApp where these conversations actually happen.
   * Where it does not exist, or the person dismisses the sheet, fall back to the
   * clipboard and say so, because a silent success is indistinguishable from the
   * button being broken again.
   */
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: listing.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      notify(t("Link copied."));
    } catch (e) {
      // AbortError is somebody closing the share sheet. That is a decision, not a
      // failure, and telling them about it would be noise.
      if (e && e.name === 'AbortError') return;
      notify(t("Could not share that. The address bar has the link."), 'caution');
    }
  };

  return (
    <>
      <BackLink label={t("Back to browse")} />
      <div className="cbm-detail" style={{ marginTop: 'var(--space-5)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: 420 }}>
          {/*
            The actual picture, which this page never showed.
            It rendered four placeholder boxes reading "card photo", "back",
            "corners" and "holo" — mockup furniture, promising three close-ups
            nobody can upload and hiding the one image we do have. So a listing
            with a perfectly good publisher photo displayed a grey rectangle.
          */}
          {/* `settled` is whether the FULL record has arrived. Without it the
              gallery would render from the browse copy and then swap. */}
          <ListingGallery listing={listing} settled={Boolean(fetched) || missing} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {takesProposals
                ? <Badge tone={listing.mode === 'trade' ? 'ink' : 'brand'}>{listing.mode === 'trade' ? t("For trade") : t("For sale")}</Badge>
                : <Badge tone="caution">{down.badge}</Badge>}
              <Tag>{gameLabel(listing.game)}</Tag>
            </div>
            <h1 className="cbm-h1" style={{ fontSize: 'var(--text-4xl)', letterSpacing: 'var(--tracking-display)' }}>{listing.title}</h1>
            <span style={{ font: 'var(--type-body)', color: 'var(--text-muted)' }}>{listing.set}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
              {/*
                The headline price. `priceCents` is CENTS and formatCents divides
                by 100 — handing it `listing.price` in euros would render €12.50
                as "0,13 €", which is wrong by two orders of magnitude and looks
                completely ordinary. The euro float no longer exists; see api.jsx.
              */}
              {listing.priceCents != null && <span style={{ font: 'var(--type-price)', fontSize: 'var(--text-2xl)', color: 'var(--text-strong)' }}>{api.formatCents(listing.priceCents)}</span>}
              <ConditionChip grade={listing.condition} label={t(GRADE_NAMES[listing.condition] || listing.condition)} showLabel />
              {/*
                The language of the card, beside its condition — both are facts
                about the object rather than terms of the offer.

                Every card has one since migration 050, so the guard below is
                belt and braces rather than a state anybody should see: a chip
                reading "unknown" or an empty label would read as a broken field.
                The name is shown in the READER's language, not the card's.
              */}
              {listing.language ? <Tag>{t(CARD_LANGUAGE_NAMES[listing.language] || listing.language)}</Tag> : null}
              {/*
                Foil, in the same row and the same component as the language.

                It belongs here for the reason the comment above gives for the
                language: a fact about the object rather than a term of the
                offer. And it has to be somewhere — a foil is a materially
                different card at a materially different price, so an advert
                that knows and does not say is one a buyer finds out about at
                the meeting.

                Shown only when it is true, like the copies badge further down
                and unlike the condition chip. "Not foil" is the answer for
                almost every card ever printed; a tag saying so on every listing
                would carry no information on the overwhelming majority of pages
                and would drown the one that does.
              */}
              {listing.foil ? <Tag>{t("Foil")}</Tag> : null}
              <DistanceTag km={listing.km} floorLabel={t("less than 1 km")} />
              {/*
                How many copies, said only where it is news.

                A listing of one is what every listing was until stock existed
                and what most still are, so "1 copy" beside every card would be
                a badge that carries no information on the majority of pages.
                More than one is worth knowing before you propose — and so is
                "none free", because that changes what a proposal means rather
                than making it impossible.
              */}
              {takesProposals && (listing.stock || {}).quantity > 1 ? (
                <Badge tone={listing.stock.available > 0 ? 'ink' : 'caution'}>
                  {listing.stock.available > 0
                    ? t("{available} of {quantity} available", { available: listing.stock.available, quantity: listing.stock.quantity })
                    : t("All {quantity} spoken for", { quantity: listing.stock.quantity })}
                </Badge>
              ) : null}
            </div>
          </div>

          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)', maxWidth: 'var(--container-prose)', textWrap: 'pretty' }}>{listing.note}</p>

          <Card padding="var(--space-5)">
            {/* The seller comes from the listing itself. There is no trade count
                or reputation yet — showing a fabricated one would be worse than
                showing none, because people would act on it. */}
            {/* A trade count is worth showing because it is earned and needs both
                sides to confirm. A report count is not shown at all — see the
                reasoning in reputation.ts; unreviewed, it is an accusation rather
                than a fact, and publishing it makes reporting the first move in
                any dispute. */}
            <TraderRow
              name={tradingName(seller)}
              // The handle is what makes two traders with the same display name
              // distinguishable, which matters most on the screen where somebody
              // decides whether to meet a stranger.
              /*
                The STORE's @ where there is one. `sellerHandle` is the owner's,
                flattened onto the listing long before stores existed — reading
                the seller directly is what keeps the name and the handle
                describing the same trader.
              */
              handle={tradingHandle(seller) ? '@' + tradingHandle(seller) : undefined}
              km={listing.km}
              distanceFloorLabel={t("less than 1 km")}
              // NOT `|| undefined`. Zero completed trades is not missing data, it
              // is the most useful thing a stranger can know about somebody they
              // are considering meeting: they are new here. Hiding it meant that in
              // a young marketplace — where everybody is on nought — reputation was
              // invisible everywhere, which is the same as not having it.
              /*
               * THE BADGE, AND IT MATTERS MORE SINCE THE NAME CHANGED.
               *
               * This row now shows a store's own name rather than the owner's.
               * Without the tick beside it, a reader has no way to tell a
               * checked store from somebody who simply typed "Cartas del
               * Barrio" as their display name — so showing the name without the
               * badge is worse than showing neither.
               *
               * Derived from the shop's PRESENCE, the same way the trader
               * endpoint does it (`verified: Boolean(row.shop_name)`): the join
               * that puts a shop here already says `verified_at IS NOT NULL`, so
               * an unverified one never arrives. One fact, read the same way in
               * both places, rather than a second flag that could disagree.
               *
               * A HARDCODED `verified={false}` SAT BELOW THIS and had to go with
               * it. JSX takes the LAST of a duplicated prop, so adding the real
               * value above it changed nothing at all and the page looked
               * exactly as it had — the kind of no-op that reads as "the data
               * must be wrong" and sends you to debug the API. It was the last
               * of the placeholders the trader page's note describes, left from
               * when there was nothing behind the prop.
               */
              verified={Boolean(seller.shop)}
              verifiedLabel={t("Verified store")}
              trades={listing.sellerTrades ?? 0}
              // The count is the row's; the sentence around it is ours, because
              // "12 trades" is not a number plus a word in every language. Browse
              // already says exactly this beside a seller, through the same key.
              tradesLabel={t("{count} trades", { count: listing.sellerTrades ?? 0 })}
              /*
               * THE WAY IN to the seller's own page, and the row is the obvious
               * place for it: somebody who has just decided they like this
               * seller is already looking at their name.
               *
               * By handle where they have one, because that is the form worth
               * sharing; by id otherwise, since a handle is optional and a page
               * nobody can link to is half a page.
               */
              onClick={() => go('trader', { id: listing.sellerHandle || seller.id })}
              /*
               * The button stops the click reaching the row. `action` renders
               * INSIDE the element carrying `onClick`, so without this, pressing
               * Message would also navigate to their page — the thread opens and
               * the screen changes out from under it.
               */
              action={takesProposals ? <Button size="sm" variant="outline" iconLeft="message-circle"
                onClick={(e) => { e.stopPropagation(); openThread(); }}>{t("Message")}</Button> : undefined} />

            {/*
              Reaching them somewhere else, if they offered.
              The icons show for EVERYBODY, including a signed-out visitor: that a
              trader is contactable is not personal data, and it is the thing that
              makes the button worth pressing. Pressing it is what needs an
              account, and a guest is offered one rather than shown nothing.
            */}
            {/*
              ONE RULE FOR THE WHOLE GROUP, not one per row.

              Reported with a screenshot: "there's a double horizontal separator
              that shouldn't be there". Each of these four rows carried its own
              `borderTop`, which was fine while there were two of them and
              stacked up the moment there were four — a shop that sells on
              Cardmarket AND keeps its stock there drew two hairlines a single
              line of text apart, which reads as a mistake because it is one.

              So the rule belongs to the SECTION. Everything under it is "more
              about this seller", separated from each other by space rather than
              by more lines. Rendered only when there is something in it, or the
              card would end on a rule under nothing.
            */}
            {seller.cardmarketUsername || showsContact
              || (seller.shop && seller.shop.primaryStock) || listing.sellerFlagged ? (
              <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-hairline)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/*
              THE CARDMARKET NAME, AS A LINK RATHER THAN A REVEAL.
              Reported: "I can set the Cardmarket handle but I don't see it in
              the listing page or the profile page." It was stored, settable and
              shown nowhere, which is the shape of a field that does nothing.

              A link and not a button, because there is nothing to reveal: the
              two below hide a phone number and a personal account behind a tap
              that needs an account, and this addresses a public page anybody can
              already read. Its own row, above them, because it answers a
              different question — not "how do I reach this person" but "what
              else do they have".

              `rel="noopener"` and `nofollow`: an outbound link to a marketplace
              we do not control, on a page anybody can publish into.
            */}
            {seller.cardmarketUsername ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {t("Also sells on")} <CardmarketMark />
                </span>
                <a
                  className="cbm-textlink"
                  href={cardmarketProfileUrl(seller.cardmarketUsername, listing.game)}
                  target="_blank" rel="noopener noreferrer nofollow"
                  style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)' }}
                >
                  {seller.cardmarketUsername}
                </a>
              </div>
            ) : null}
            {showsContact ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <span style={{ flex: 1, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {t("Also reachable on")}
                </span>
                {reachable.telegram ? (
                  <Button size="sm" variant="ghost" iconLeft="send" disabled={revealing === 'telegram'}
                    onClick={() => reveal('telegram')}>Telegram</Button>
                ) : null}
                {reachable.whatsapp ? (
                  <Button size="sm" variant="ghost" iconLeft="message-circle" disabled={revealing === 'whatsapp'}
                    onClick={() => reveal('whatsapp')}>WhatsApp</Button>
                ) : null}
              </div>
            ) : null}
            {/*
              WHERE THIS SHOP'S STOCK REALLY LIVES, said before anybody travels.

              The owner: "if set, listings of that shop should show a warning
              saying their stock is primarily handled somewhere else and that
              means the listing in our platform may be outdated or cancelled in
              case of duplicated selling."

              A SHOP WITH THREE HUNDRED CARDS DOES NOT RETYPE THEM HERE — it
              imports, and an import is a snapshot. Between imports the other
              marketplace keeps selling, so a card showing here can already be
              gone. Until the sync exists this sentence is the only truthful
              thing we can offer, and it belongs next to the seller rather than
              buried: a buyer should read it before tapping, not after arriving
              at a counter.

              Named rather than vague. "Somewhere else" invites a guess; a buyer
              who is told it is Cardmarket can go and look.
            */}
            {seller.shop && seller.shop.primaryStock ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Icon name="alert-triangle" size={16} />
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
                  {t("This shop keeps its stock on {market}. A card sold there can still be showing here, so check with them before you travel.",
                    { market: seller.shop.primaryStock === 'cardmarket' ? 'Cardmarket' : 'CardTrader' })}
                  {/*
                    Shown beside the sentence rather than instead of the word.
                    This is the place the confusion actually bites — somebody
                    skimming a warning about where the stock really is — so the
                    name stays in words for anybody reading carefully and the
                    wordmark settles it for anybody not. `alt=""` because the
                    sentence has already said it; announcing it twice is noise.

                    CardTrader has no mark here, which is honest rather than
                    untidy: we were given one logo, and inventing a treatment
                    for the other would be putting words in their mouth.
                  */}
                  {/*
                    Both marks now, which is what makes the pair honest: one
                    brand getting a logo and the other a bare word reads as a
                    preference we do not have. CardTrader's needs its own dark
                    ground — see `CardTraderMark` — because their asset is
                    white-on-dark and half the name vanishes on ours.
                  */}
                  {seller.shop.primaryStock === 'cardmarket'
                    ? <> <CardmarketMark height={12} alt="" /></>
                    : <> <CardTraderMark height={11} alt="" /></>}
                </span>
              </div>
            ) : null}
            {listing.sellerFlagged ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Icon name="shield-check" size={16} />
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
                  {t("Other traders have reported problems with this account. Nothing here says you should not trade — meet somewhere public, check the card carefully, and take your time.")}
                </span>
              </div>
            ) : null}
              </div>
            ) : null}
          </Card>

          {/*
            The suggested meeting points were four invented cafés. Showing a real
            person an address we made up is worse than showing nothing: they might
            go there. Where to meet is agreed in the trade, by the two people who
            have to find it.
          */}

          {/*
            Your own listing offers you your own controls.
            Proposing a trade to yourself was reachable through the entire form
            and only refused at the server — so somebody could pick their cards,
            suggest a place and a time, and be told no at the last step. A door
            that opens onto a wall.
          */}
          {/*
            ONLY WHAT THE BADGE CANNOT SAY.
            
            REPORTED: "here we should only show the information about copies
            once. Now we show it with the black tag that says '2 OF 2 AVAILABLE'
            and with the label that says '2 copies.' at the bottom."
            
            Quite right — those were the same fact twice, in two places, in two
            registers. The badge beside the price is the one that stays, because
            it sits with the rest of what this listing IS.
            
            WHAT SURVIVES IS THE COMMITTED LINE, and it is not a duplicate: the
            badge counts what is AVAILABLE, and this says how many of the others
            are promised to trades already agreed. That is a different fact, it
            is the one that explains why the number shrank, and it is the only
            thing here a seller cannot work out from the badge.
            
            It also covers the single copy the badge refuses to draw. The badge
            hides itself below two — "1 of 1" is noise on the majority of pages
            — so a seller whose one copy is spoken for would otherwise be told
            nothing at all about it.
          */}
          {listing.stock && listing.stock.committed ? (
            <p style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-3)' }}>
              {t("{available} of {quantity} free — {committed} promised to trades already agreed.", { available: listing.stock.available, quantity: listing.stock.quantity, committed: listing.stock.committed })}
            </p>
          ) : null}
          {takesProposals ? null : (
            <p role="status" style={{ margin: 0, font: 'var(--type-body-sm)', color: 'var(--text-body)' }}>
              {down.notice}
            </p>
          )}
          <div className="cbm-actionbar">
            {seller.isYou ? (
              <Button size="lg" variant="outline" onClick={() => go('you', { tab: 'selling' })}>{t("Manage your listings")}</Button>
            ) : takesProposals ? (
              <>
                <Button size="lg" iconLeft="arrow-left-right" onClick={() => setPropose(true)}>{t("Propose a trade")}</Button>
              </>
            ) : null}
            {/* An operator's correction (FixListingDialog). Hiding it is a courtesy, not
                the protection: the server refuses anybody else. Only on a listing that is up. */}
            {trader && trader.isAdmin && takesProposals && fetched ? (
              <Button size="lg" variant="ghost" onClick={() => setFixing(true)}>{t("Fix this listing")}</Button>
            ) : null}
            <IconButton icon="share-2" label={t("Share listing")} size="lg" onClick={share} />
          </div>
        </div>
      </div>

      {more.length ? (
        <section style={{ marginTop: 'var(--space-11)' }}>
          <FluteDivider style={{ marginBottom: 'var(--space-6)' }} />
          <h2 className="cbm-h2" style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-5)' }}>{t("More from {name}", { name: tradingName(seller) })}</h2>
          <div className="cbm-grid-listings">
            {more.map((l) => (
              <Linked key={l.id} to={{ name: 'listing', id: l.id }}>
                <ListingCard {...api.listingCardProps(l, t)} />
              </Linked>
            ))}
          </div>
        </section>
      ) : null}

      <ProposeTradeDialog listing={listing} open={propose} onClose={() => setPropose(false)} />
      {fixing ? (
        <FixListingDialog listing={listing} onClose={() => setFixing(false)}
          onFixed={() => { setFixing(false); setRevision((n) => n + 1); }} />
      ) : null}
    </>
  );
}

